/**
 * MapLibre GL JS helper utilities.
 * Provides idempotent source/layer management and WMS URL building.
 */

/**
 * Add or update a GeoJSON source. If source exists, updates data; otherwise creates it.
 */
export function addOrUpdateGeoJsonSource(map, sourceId, data) {
    var src = map.getSource(sourceId);
    if (src) {
        src.setData(data);
    } else {
        map.addSource(sourceId, { type: 'geojson', data: data });
    }
}

/**
 * Remove MapLibre layers and their source.
 * @param {maplibregl.Map} map
 * @param {string} sourceId
 * @param {string[]} layerIds
 */
export function removeSourceAndLayers(map, sourceId, layerIds) {
    if (!map) return;
    if (layerIds) {
        for (var i = 0; i < layerIds.length; i++) {
            if (map.getLayer(layerIds[i])) {
                map.removeLayer(layerIds[i]);
            }
        }
    }
    if (sourceId && map.getSource(sourceId)) {
        // Remove any remaining layers that reference this source
        var style = map.getStyle();
        if (style && style.layers) {
            for (var j = style.layers.length - 1; j >= 0; j--) {
                if (style.layers[j].source === sourceId) {
                    map.removeLayer(style.layers[j].id);
                }
            }
        }
        map.removeSource(sourceId);
    }
}

/**
 * Set layer visibility ('visible' or 'none').
 */
export function setVisibility(map, layerId, visible) {
    if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    }
}

/**
 * Build a WMS tile URL template for MapLibre raster source.
 * Uses {bbox-epsg-3857} placeholder which MapLibre substitutes automatically.
 *
 * @param {string} baseUrl - WMS service base URL
 * @param {Object} params - WMS parameters (LAYERS, FORMAT, VERSION, etc.)
 * @returns {string} Full URL template
 */
export function wmsSourceTiles(baseUrl, params) {
    var version = params.VERSION || params.version || '1.1.1';
    var srs = parseFloat(version) >= 1.3 ? 'CRS' : 'SRS';

    var queryParts = [
        'SERVICE=WMS',
        'REQUEST=GetMap',
        'VERSION=' + version,
        'LAYERS=' + encodeURIComponent(params.LAYERS || params.layers || ''),
        'FORMAT=' + encodeURIComponent(params.FORMAT || params.format || 'image/png'),
        'TRANSPARENT=' + (params.TRANSPARENT != null ? params.TRANSPARENT : (params.transparent != null ? params.transparent : 'true')),
        srs + '=EPSG:3857',
        'WIDTH=' + (params.WIDTH || params.width || 256),
        'HEIGHT=' + (params.HEIGHT || params.height || 256),
        'BBOX={bbox-epsg-3857}'
    ];

    queryParts.push('STYLES=' + encodeURIComponent(params.STYLES || params.styles || ''));

    var separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
    return baseUrl + separator + queryParts.join('&');
}

/**
 * Ensure layers are in the specified visual order (first = bottom, last = top).
 * Only moves layers that exist on the map.
 * Replaces bringDataLayersToFront() pattern.
 *
 * @param {maplibregl.Map} map
 * @param {string[]} layerIds - Layer IDs in desired bottom-to-top order
 */
export function ensureLayerOrder(map, layerIds) {
    var existing = [];
    for (var i = 0; i < layerIds.length; i++) {
        if (map.getLayer(layerIds[i])) {
            existing.push(layerIds[i]);
        }
    }
    for (var j = 1; j < existing.length; j++) {
        map.moveLayer(existing[j]);
    }
}

/**
 * Register a pointer cursor on interactive layers. `layerId` may be an array:
 * MapLibre then treats the layers as one area for enter and leave.
 */
export function registerCursorHandlers(map, layerId) {
    map.on('mouseenter', layerId, function() {
        map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', layerId, function() {
        map.getCanvas().style.cursor = '';
    });
}
